import * as React from "react";
import { Alert } from "antd";
import { ExternalLink } from "../../newcomponents/table/components/ExternalLink";
import { DOCS_URL, AGENT_UPDATE_INTERVAL_MS } from "../../envVariables";
import { CreatedColumn } from "../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { Table } from "../../newcomponents/table/table";
import { useTableItemQueryData } from "../../newcomponents/table/data/useTableItemQueryData";
import { useTableKindId } from "../../newcomponents/table/data/useTableKindId";
import { TableNewItemButton } from "../../newcomponents/table/components/tableNewButton";
import { TableHeaderRefreshButton } from "../../newcomponents/table/components/RefreshButton";
import { CustomTableCPLNQuery } from "../../newcomponents/table/components/TableCplnQuery";
import { NameDescriptionColumn } from "../../newcomponents/table/columns/wellKnown/nameDescriptionColumn";
import { TagsColumn } from "../../newcomponents/table/columns/wellKnown/tagsColumn";
import { AgentLastActiveTextColumn } from "../../newcomponents/table/columns/agent/lastActiveText";
import { Agent } from "../../schema/types/agent/entity";
import { AgentTableItem, getAgentTableItem } from "../../mst/kinds/agent.helpers";
import { useAgentInfosReducer } from "../../components/detail/agentInfosReducer";
import { TableActionsDropdown } from "../../newcomponents/table/components/tableActionsDropdown";
import { DeleteModal } from "../../components/modals/deleteModal";
import { useColumnsWithTagColumns } from "../../newcomponents/table/columns/tagColumns/useColumnsWithTagColumns";

export const AgentList: React.FC = () => {
  const { state, fetchAgentInfos } = useAgentInfosReducer();

  const [selectedLinks, setSelectedLinks] = React.useState<string[]>([]);
  const [action, setAction] = React.useState<string>("");

  const { kind, id } = useTableKindId("agent", "agent-list-update1");
  const qData = useTableItemQueryData<Agent>(kind, {});
  const columns = useColumnsWithTagColumns<AgentTableItem>(
    [NameDescriptionColumn(), AgentLastActiveTextColumn(), TagsColumn(), CreatedColumn(), LastModifiedColumn()],
    kind,
  );

  React.useEffect(() => {
    const unsubscribe = fetchAgentInfos(
      qData.visibleItems.map((i) => i.name),
      AGENT_UPDATE_INTERVAL_MS,
    );
    return () => {
      unsubscribe();
    };
  }, [qData.visibleItems.length]);

  const hasV1Agent = qData.items.some((agent) => agent.status?.protocolVersion !== "v2");

  const agentTableItems = qData.visibleItems.map((agent) => {
    const agentInfo = state.agentInfoMap[agent.name];
    return getAgentTableItem({ item: agent, info: agentInfo });
  });

  return (
    <>
      {hasV1Agent ? (
        <Alert
          className="mb-4"
          type="warning"
          showIcon
          message={
            <>
              Starting April 13, new agents created will speak protocol version 2.{" "}
              <ExternalLink url="https://docs.controlplane.com/reference/agent#protocol-version-2" label="Learn more" disableCopy />
            </>
          }
        />
      ) : null}
      <div className={`mb-4 flex items-center justify-between border p-8 image-info-box`}>
        <div>You can configure an agent to allow access to resources on other clouds or private networks.</div>
        <ExternalLink
          className="ml-2"
          url={`${DOCS_URL}/reference/agent`}
          label="How to set up an Agent?"
          disableCopy
        />
      </div>
      <Table<AgentTableItem>
        title={"Agents"}
        data={agentTableItems}
        selectKey="selfLink"
        selectMode="multi"
        selections={selectedLinks}
        onSelectionsChange={setSelectedLinks}
        tableId={id}
        headerRenderer={() => {
          return (
            <>
              <TableNewItemButton kind={kind} />
              <TableActionsDropdown
                kind={kind}
                selectionLinks={selectedLinks}
                options={[
                  {
                    key: "delete",
                    label: "Delete",
                    disabled: selectedLinks.length < 1,
                    danger: true,
                    callback: () => setAction("delete"),
                  },
                ]}
              />
              <TableHeaderRefreshButton onClick={qData.refetch} />
              <CustomTableCPLNQuery
                tableId={id}
                kind={kind}
                onQuery={qData.setCplnQuery}
                isLoading={qData.isLoading}
              />
            </>
          );
        }}
        enableFilter
        isLoading={qData.isLoading}
        onSortChange={qData.setSort}
        onSearchChange={qData.setSearch}
        isLoadingMore={qData.isLoadingMore}
        hasMore={qData.hasMore}
        onLoadMore={qData.fetchNextPage}
        columns={columns}
      />
      {action === "delete" ? (
        <DeleteModal
          kind={kind}
          onCancel={() => setAction("")}
          onComplete={() => {
            setAction("");
            setSelectedLinks([]);
            qData.refetch();
          }}
          links={selectedLinks}
        />
      ) : null}
    </>
  );
};
